import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import {
  coursesService,
  lessonsService,
  enrollmentsService,
  lessonProgressService,
  evaluationsService,
} from '../config/supabase';
import '../styles/CoursePage.css';

const CompletionModal = ({ course, onDownload, downloadingPdf, onClose }) => (
  <div className="cp-overlay" onClick={onClose}>
    <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
      <div className="cp-trophy">&#127942;</div>
      <h2 className="cp-modal-h2">Curso Completado</h2>
      <p className="cp-modal-sub">Has terminado exitosamente:</p>
      <h3 className="cp-modal-name">{course.titulo}</h3>
      <div className="cp-modal-cert">Certificado de Finalizacion</div>

      <div className="cp-modal-actions">
        <button className="cp-modal-cta" onClick={onDownload} disabled={downloadingPdf}>
          {downloadingPdf ? 'Generando PDF...' : 'Descargar Certificado PDF'}
        </button>
        <button className="cp-modal-ghost" onClick={onClose}>
          Volver al Dashboard
        </button>
      </div>
    </div>
  </div>
);

const CoursePage = () => {
  const DEFAULT_IFRAME_MIN_WATCH_SECONDS = 120;
  const LOCK_MARKER_REGEX = /<!--\s*lock_seconds:(\d+)\s*-->/i;

  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const contentRef = useRef(null);

  const [course, setCourse]       = useState(null);
  const [lecciones, setLecciones] = useState([]);
  const [current, setCurrent]     = useState(0);
  const [doneIds, setDoneIds]     = useState([]);
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [evaluationAnswers, setEvaluationAnswers] = useState({});
  const [evaluationAttempt, setEvaluationAttempt] = useState(null);
  const [submittingEvaluation, setSubmittingEvaluation] = useState(false);
  const [videoGate, setVideoGate] = useState({
    isRequired: false,
    isCompleted: true,
    mode: null, // native | iframe | null
    requiredSeconds: DEFAULT_IFRAME_MIN_WATCH_SECONDS,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const access = await enrollmentsService.hasAccess(courseId);
        if (!access) { navigate('/dashboard'); return; }

        const [courseData, lessonsData] = await Promise.all([
          coursesService.getById(courseId),
          lessonsService.getByCourse(courseId),
        ]);
        if (!courseData) { navigate('/dashboard'); return; }

        const ids  = (lessonsData || []).map((l) => l.id);
        const done = await lessonProgressService.getCompleted(ids).catch(() => []);
        const evaluationData = await evaluationsService.getByCourse(courseId).catch(() => null);
        const latestAttempt = evaluationData?.id
          ? await evaluationsService.getLatestAttempt(evaluationData.id).catch(() => null)
          : null;

        setCourse(courseData);
        setLecciones(lessonsData || []);
        setDoneIds(done);
        setEvaluation(evaluationData);
        setEvaluationAttempt(latestAttempt);
        setEvaluationAnswers({});

        const firstPending = (lessonsData || []).findIndex((l) => !done.includes(l.id));
        setCurrent(firstPending >= 0 ? firstPending : 0);
      } catch (err) {
        console.error(err);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId, navigate]);

  const evaluationIndex = lecciones.length;
  const shouldShowEvaluation = lecciones.length > 0 && doneIds.length === lecciones.length && evaluation?.questions?.length > 0;
  const isEvaluationView = current === evaluationIndex;

  const isAccessible = useCallback(
    (i) => {
      if (i < 0) return false;
      if (i < lecciones.length) {
        return i === 0 || doneIds.includes(lecciones[i - 1]?.id);
      }
      if (i === evaluationIndex) {
        return shouldShowEvaluation;
      }
      return false;
    },
    [doneIds, lecciones, evaluationIndex, shouldShowEvaluation]
  );

  const goTo = useCallback((i) => {
    if (i >= 0 && i <= evaluationIndex && isAccessible(i)) {
      setCurrent(i);
      if (contentRef.current) contentRef.current.scrollTop = 0;
    }
  }, [evaluationIndex, isAccessible]);

  const markComplete = async () => {
    if (saving) return;
    const lesson = lecciones[current];
    if (!lesson) return;
    const wasDone = doneIds.includes(lesson.id);
    setSaving(true);
    try {
      if (wasDone) {
        await lessonProgressService.markIncomplete(lesson.id);
        setDoneIds((p) => p.filter((id) => id !== lesson.id));
      } else {
        await lessonProgressService.markComplete(lesson.id);
        const updated = [...doneIds, lesson.id];
        setDoneIds(updated);
        if (updated.length === lecciones.length) {
          if (evaluation?.questions?.length > 0 && !evaluationAttempt?.passed) {
            setTimeout(() => goTo(evaluationIndex), 300);
          } else {
            setTimeout(() => setShowModal(true), 500);
          }
          return;
        }
        setTimeout(() => goTo(current + 1), 600);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCertificate = useCallback(() => {
    if (!course || !user) return;

    try {
      setDownloadingPdf(true);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      const innerW = pageWidth - margin * 2;
      const studentName = user.user_metadata?.full_name || user.email || 'Estudiante';
      const dateText = new Date().toLocaleDateString('es-MX', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
      const code = ('CERT-' + courseId.slice(0, 8) + '-' + user.id.slice(0, 8)).toUpperCase();

      doc.setDrawColor(102, 126, 234);
      doc.setLineWidth(1.5);
      doc.roundedRect(margin, margin, innerW, pageHeight - margin * 2, 3, 3, 'S');

      doc.setDrawColor(224, 231, 255);
      doc.setLineWidth(0.5);
      doc.roundedRect(margin + 4, margin + 4, innerW - 8, pageHeight - (margin + 4) * 2, 3, 3, 'S');

      doc.setTextColor(76, 29, 149);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(28);
      doc.text('CERTIFICADO', pageWidth / 2, 44, { align: 'center' });

      doc.setTextColor(71, 85, 105);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(14);
      doc.text('Se certifica que', pageWidth / 2, 58, { align: 'center' });

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.text(studentName, pageWidth / 2, 74, { align: 'center', maxWidth: 240 });

      doc.setTextColor(71, 85, 105);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(13);
      doc.text('ha completado satisfactoriamente el curso', pageWidth / 2, 88, { align: 'center' });

      doc.setTextColor(30, 41, 59);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(19);
      doc.text(course.titulo, pageWidth / 2, 103, { align: 'center', maxWidth: 240 });

      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text('Fecha de emision: ' + dateText, pageWidth / 2, 120, { align: 'center' });

      doc.setDrawColor(203, 213, 225);
      doc.line(pageWidth / 2 - 40, 142, pageWidth / 2 + 40, 142);
      doc.setFontSize(10);
      doc.text('Academia', pageWidth / 2, 148, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text('Codigo de verificacion: ' + code, pageWidth / 2, pageHeight - 18, { align: 'center' });

      const safeCourse = (course.titulo || 'curso')
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '');
      doc.save('certificado-' + safeCourse + '.pdf');
    } finally {
      setDownloadingPdf(false);
    }
  }, [course, courseId, user]);

  const normalizeLessonContent = useCallback((html) => {
    if (!html) return '';

    // Normaliza Drive a iframe preview para asegurar reproduccion en moviles.
    const normalized = html
      .replace(/^https?:\/\/drive\.google\.com\/[^\s<]+\s*/i, '')
      .replace(
        /<iframe[^>]*src=["']https?:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/(?:preview|view)[^"']*["'][^>]*><\/iframe>/gi,
        '<iframe src="https://drive.google.com/file/d/$1/preview?rm=minimal" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>'
      )
      .replace(
        /<video[^>]*src=["']https?:\/\/drive\.google\.com\/uc\?export=(?:download|view)&id=([a-zA-Z0-9_-]+)[^"']*["'][^>]*><\/video>/gi,
        '<iframe src="https://drive.google.com/file/d/$1/preview?rm=minimal" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>'
      )
      .replace(
        /(<p>\s*)?<iframe\s+src="https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/preview(?:\?rm=minimal)?"[^>]*><\/iframe>(\s*<\/p>)?/gi,
        '<iframe src="https://drive.google.com/file/d/$2/preview?rm=minimal" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>'
      )
      // Si por ediciones previas quedaron duplicados del mismo video, conserva solo uno.
      .replace(
        /(<iframe\s+src="https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/preview(?:\?rm=minimal)?"[^>]*><\/iframe>\s*){2,}/gi,
        '<iframe src="https://drive.google.com/file/d/$2/preview?rm=minimal" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>'
      )
      // Envuelve el embed de Drive con boton para abrir reproductor nativo.
      .replace(
        /<iframe\s+src="https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/preview(?:\?rm=minimal)?"[^>]*><\/iframe>/gi,
        '<div class="cp-drive-embed"><iframe src="https://drive.google.com/file/d/$1/preview?rm=minimal" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe><a class="cp-drive-open" href="https://drive.google.com/file/d/$1/view?usp=drivesdk" target="_blank" rel="noopener noreferrer">Abrir en Drive (pantalla completa)</a></div>'
      );

    return normalized;
  }, []);

  useEffect(() => {
    const currentLesson = lecciones[current];
    if (!currentLesson?.contenido) {
      setVideoGate({ isRequired: false, isCompleted: true, mode: null });
      return;
    }

    const normalized = normalizeLessonContent(currentLesson.contenido);
    const hasNativeVideo = /<video[\s>]/i.test(normalized);
    const hasIframeVideo = /<iframe[^>]+src=["'][^"']*(youtube\.com|youtu\.be|drive\.google\.com)[^"']*["']/i.test(normalized);

    if (hasNativeVideo) {
      setVideoGate({ isRequired: true, isCompleted: false, mode: 'native', requiredSeconds: DEFAULT_IFRAME_MIN_WATCH_SECONDS });
      return;
    }
    if (hasIframeVideo) {
      const lockMatch = currentLesson.contenido.match(LOCK_MARKER_REGEX);
      const requiredSeconds = lockMatch
        ? Math.max(0, parseInt(lockMatch[1], 10) || 0)
        : DEFAULT_IFRAME_MIN_WATCH_SECONDS;
      // En iframes no se puede inspeccionar progreso interno por seguridad del navegador.
      setVideoGate({ isRequired: true, isCompleted: false, mode: 'iframe', requiredSeconds });
      return;
    }

    setVideoGate({ isRequired: false, isCompleted: true, mode: null, requiredSeconds: DEFAULT_IFRAME_MIN_WATCH_SECONDS });
  }, [current, lecciones, normalizeLessonContent]);

  useEffect(() => {
    if (!videoGate.isRequired || videoGate.mode !== 'native') return;
    const root = contentRef.current;
    if (!root) return;
    const videos = Array.from(root.querySelectorAll('.cp-html video'));
    if (videos.length === 0) return;

    const checkCompleted = () => {
      const completed = videos.every((v) => v.ended || (v.duration > 0 && v.currentTime >= v.duration - 0.2));
      setVideoGate((prev) => ({ ...prev, isCompleted: completed }));
    };

    const subscriptions = videos.map((video) => {
      const handler = () => checkCompleted();
      video.addEventListener('ended', handler);
      video.addEventListener('timeupdate', handler);
      return { video, handler };
    });

    checkCompleted();

    return () => {
      subscriptions.forEach(({ video, handler }) => {
        video.removeEventListener('ended', handler);
        video.removeEventListener('timeupdate', handler);
      });
    };
  }, [current, lecciones, videoGate.isRequired, videoGate.mode]);

  useEffect(() => {
    if (!videoGate.isRequired || videoGate.mode !== 'iframe' || videoGate.isCompleted) return;

    let elapsed = 0;
    const intervalId = setInterval(() => {
      if (document.hidden) return;
      elapsed += 1;
      if (elapsed >= videoGate.requiredSeconds) {
        setVideoGate((gate) => ({ ...gate, isCompleted: true }));
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [videoGate.isRequired, videoGate.mode, videoGate.isCompleted, videoGate.requiredSeconds]);

  const handleSelectEvaluationOption = (questionId, optionId) => {
    setEvaluationAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
  };

  const handleSubmitEvaluation = async () => {
    if (!evaluation?.questions?.length) return;
    const unanswered = evaluation.questions.filter((question) => !evaluationAnswers[question.id]);
    if (unanswered.length > 0) {
      alert('Responde todas las preguntas antes de enviar la evaluacion.');
      return;
    }

    const correctCount = evaluation.questions.reduce((acc, question) => {
      const correctOption = (question.options || []).find((option) => option.is_correct);
      return acc + (correctOption && evaluationAnswers[question.id] === correctOption.id ? 1 : 0);
    }, 0);
    const score = Math.round((correctCount / evaluation.questions.length) * 100);
    const passed = score >= (evaluation.passing_score || 70);

    setSubmittingEvaluation(true);
    try {
      const attempt = await evaluationsService.createAttempt({
        evaluationId: evaluation.id,
        courseId,
        score,
        passed,
        answers: evaluationAnswers,
      });
      setEvaluationAttempt(attempt);
      if (passed) {
        setTimeout(() => setShowModal(true), 300);
      }
    } catch (error) {
      alert('Error enviando evaluacion: ' + error.message);
    } finally {
      setSubmittingEvaluation(false);
    }
  };

  if (loading || !course) {
    return (
      <div className="cp-page">
        <Header />
        <div className="cp-loading">
          <div className="cp-spinner" />
          <span>Cargando curso...</span>
        </div>
      </div>
    );
  }

  const lesson  = lecciones[current];
  const pct     = lecciones.length > 0 ? Math.round((doneIds.length / lecciones.length) * 100) : 0;
  const isDone  = lesson && doneIds.includes(lesson.id);
  const canNext = current < lecciones.length - 1 && isAccessible(current + 1);
  const canPrev = current > 0;
  const allLessonsDone = lecciones.length > 0 && doneIds.length === lecciones.length;
  const canReprintCertificate = allLessonsDone && (!evaluation?.questions?.length || evaluationAttempt?.passed);

  return (
    <div className="cp-page">
      <Header />

      {showModal && (
        <CompletionModal
          course={course}
          onDownload={handleDownloadCertificate}
          downloadingPdf={downloadingPdf}
          onClose={() => { setShowModal(false); navigate('/dashboard'); }}
        />
      )}

      <div className="cp-course-bar">
        <button className="cp-back" onClick={() => navigate('/dashboard')}>
          &larr; Dashboard
        </button>
        <div className="cp-course-bar-center">
          <span className="cp-course-bar-title">{course.titulo}</span>
        </div>
        <div className="cp-course-bar-right">
          <div className="cp-bar-track">
            <div className="cp-bar-fill" style={{ width: pct + '%' }} />
          </div>
          <span className="cp-bar-label">{doneIds.length}/{lecciones.length} &middot; {pct}%</span>
          {canReprintCertificate && (
            <button className="cp-btn-reprint" onClick={handleDownloadCertificate} disabled={downloadingPdf}>
              {downloadingPdf ? 'Generando...' : 'Reimprimir certificado'}
            </button>
          )}
        </div>
      </div>

      <div className="cp-layout">

        <nav className="cp-nav">
          <div className="cp-nav-label">CONTENIDO DEL CURSO</div>

          <div className="cp-nav-list">
            {lecciones.map((l, i) => {
              const done       = doneIds.includes(l.id);
              const accessible = isAccessible(i);
              const active     = current === i;
              return (
                <button
                  key={l.id}
                  disabled={!accessible}
                  onClick={() => goTo(i)}
                  className={
                    'cp-nav-item' +
                    (active      ? ' cp-nav-item--active' : '') +
                    (done        ? ' cp-nav-item--done'   : '') +
                    (!accessible ? ' cp-nav-item--locked' : '')
                  }
                >
                  <span className="cp-nav-icon">
                    {done ? 'OK' : !accessible ? 'X' : active ? '>' : String(i + 1)}
                  </span>
                  <div className="cp-nav-text">
                    <span className="cp-nav-num">Leccion {i + 1}</span>
                    <span className="cp-nav-title">{l.titulo}</span>
                  </div>
                </button>
              );
            })}
            {shouldShowEvaluation && (
              <button
                key="evaluation-item"
                disabled={!isAccessible(evaluationIndex)}
                onClick={() => goTo(evaluationIndex)}
                className={
                  'cp-nav-item' +
                  (current === evaluationIndex ? ' cp-nav-item--active' : '') +
                  (evaluationAttempt?.passed ? ' cp-nav-item--done' : '')
                }
              >
                <span className="cp-nav-icon">E</span>
                <div className="cp-nav-text">
                  <span className="cp-nav-num">Evaluación</span>
                  <span className="cp-nav-title">{evaluation?.title || 'Evaluación final'}</span>
                </div>
              </button>
            )}
          </div>

          <div className="cp-ring-wrap">
            <div className="cp-ring-box">
              <svg viewBox="0 0 36 36" className="cp-ring-svg">
                <path className="cp-ring-bg"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" strokeWidth="3" />
                <path className="cp-ring-fg"
                  strokeDasharray={pct + ', 100'}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" strokeWidth="3" />
              </svg>
              <span className="cp-ring-num">{pct}%</span>
            </div>
            <span className="cp-ring-label">{doneIds.length} de {lecciones.length} lecciones</span>
          </div>
        </nav>

        <main className="cp-content" ref={contentRef}>
          {isEvaluationView ? (
            <section className="cp-eval-card">
              <div className="cp-eval-head">
                <div>
                  <span className="cp-eval-kicker">Evaluación final</span>
                  <h2>{evaluation?.title || 'Evaluación final'}</h2>
                  {evaluation?.description && <p>{evaluation.description}</p>}
                </div>
                <div className="cp-eval-meta">
                  <span>Minimo: {evaluation?.passing_score || 70}%</span>
                  {evaluationAttempt && (
                    <span className={evaluationAttempt.passed ? 'cp-eval-status ok' : 'cp-eval-status bad'}>
                      {evaluationAttempt.passed ? 'Aprobada' : 'Intento previo: ' + evaluationAttempt.score + '%'}
                    </span>
                  )}
                </div>
              </div>

              <div className="cp-eval-list">
                {evaluation?.questions.map((question, index) => (
                  <div key={question.id} className="cp-eval-question">
                    <h3>{index + 1}. {question.prompt}</h3>
                    <div className="cp-eval-options">
                      {(question.options || []).map((option) => (
                        <label key={option.id} className="cp-eval-option">
                          <input
                            type="radio"
                            name={'question-' + question.id}
                            checked={evaluationAnswers[question.id] === option.id}
                            onChange={() => handleSelectEvaluationOption(question.id, option.id)}
                          />
                          <span>{option.option_text}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="cp-eval-actions">
                <button className="cp-btn-complete" onClick={handleSubmitEvaluation} disabled={submittingEvaluation}>
                  {submittingEvaluation ? 'Enviando...' : 'Enviar evaluación'}
                </button>
                {evaluationAttempt && !evaluationAttempt.passed && (
                  <p className="cp-eval-feedback bad">Tu último resultado fue {evaluationAttempt.score}%. Puedes intentar de nuevo.</p>
                )}
                {evaluationAttempt?.passed && (
                  <p className="cp-eval-feedback ok">Evaluación aprobada con {evaluationAttempt.score}%.</p>
                )}
              </div>
            </section>
          ) : !lesson ? (
            <div className="cp-empty">
              <p>Selecciona una leccion para comenzar</p>
            </div>
          ) : (
            <React.Fragment>
              <div className="cp-lesson-head">
                <div className="cp-lesson-meta">
                  <span className="cp-lesson-num">Leccion {current + 1} de {lecciones.length}</span>
                  {isDone && <span className="cp-lesson-done-tag">Completada</span>}
                </div>
                <h1 className="cp-lesson-h1">{lesson.titulo}</h1>
                {lesson.descripcion && <p className="cp-lesson-p">{lesson.descripcion}</p>}
              </div>

              <div className="cp-lesson-body">
                {lesson.contenido
                  ? <div className="cp-html" dangerouslySetInnerHTML={{ __html: normalizeLessonContent(lesson.contenido) }} />
                  : (
                    <div className="cp-no-content">
                      <p>Esta leccion no tiene contenido aun.</p>
                    </div>
                  )
                }
              </div>

              <div className="cp-footer">
                {videoGate.isRequired && !videoGate.isCompleted && (
                  <div className="cp-video-gate">
                    <p className="cp-video-gate-text">
                      Debes terminar el video para habilitar "Completar leccion".
                    </p>
                  </div>
                )}

                <button className="cp-btn-prev" onClick={() => goTo(current - 1)} disabled={!canPrev}>
                  &larr; Anterior
                </button>

                <button
                  className={'cp-btn-complete' + (isDone ? ' cp-btn-complete--undo' : '')}
                  onClick={markComplete}
                  disabled={saving || (videoGate.isRequired && !videoGate.isCompleted)}
                >
                  {saving ? '...' : isDone ? 'Marcar incompleta' : 'Completar leccion'}
                </button>

                <button className="cp-btn-next" onClick={() => goTo(current + 1)} disabled={!canNext}>
                  Siguiente &rarr;
                </button>
              </div>
            </React.Fragment>
          )}
        </main>
      </div>
    </div>
  );
};

export default CoursePage;